import { DateInput } from "@/common/components/DateInput/DateInput";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import TextField from "@/common/components/TextField";
import { extractDateTime, toIsoFromDateTimeParts } from "@/common/utils/dateUtils";
import { formatDateTime } from "@/common/utils/formatters";
import { FormTransformers } from "@/common/utils/formTransformers";
import ConfirmUpdateAuctionDates, { AuctionDatesChange } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/components/modals/ConfirmUpdateAuctionDates";
import { useMaintenanceEstablishmentChoiceAuctionEntryUpdateDates } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/hooks/useMaintenanceEstablishmentChoiceAuctionEntryUpdateDates";
import { auctionEntryUpdateDatesSchema, AuctionEntryUpdateDatesValidationSchema } from "@/features/maintenanceWorkflow/pages/Show/features/establishmentChoiceEntries/features/auction/schemas/auctionEntryUpdateDates.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { ClockIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

interface UpdateAuctionDatesProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  establishmentChoiceEntryId: string | undefined;
  forwardingId: string | undefined;
  startAt: string | null | undefined;
  finishAt: string | null | undefined;
}

const buildAuctionDatesChanges = (
  data: AuctionEntryUpdateDatesValidationSchema,
  startAt: string | null | undefined,
  finishAt: string | null | undefined,
): AuctionDatesChange[] => {
  const changes: AuctionDatesChange[] = [];

  const startBeforeDate = extractDateTime(startAt, 'date');
  const startBeforeTime = extractDateTime(startAt, 'time');

  if (
    data.start_at_date && data.start_at_time
    && (data.start_at_date !== startBeforeDate || data.start_at_time !== startBeforeTime)
  ) {
    changes.push({
      label: 'Início do leilão',
      before: startAt ? formatDateTime(new Date(startAt)) : '--',
      after: formatDateTime(new Date(toIsoFromDateTimeParts(data.start_at_date, data.start_at_time))),
    });
  }

  const finishBeforeDate = extractDateTime(finishAt, 'date');
  const finishBeforeTime = extractDateTime(finishAt, 'time');

  if (
    data.finish_at_date && data.finish_at_time
    && (data.finish_at_date !== finishBeforeDate || data.finish_at_time !== finishBeforeTime)
  ) {
    changes.push({
      label: 'Fim do leilão',
      before: finishAt ? formatDateTime(new Date(finishAt)) : '--',
      after: formatDateTime(new Date(toIsoFromDateTimeParts(data.finish_at_date, data.finish_at_time))),
    });
  }

  return changes;
};

const UpdateAuctionDates = ({
  isOpen,
  setIsOpen,
  establishmentChoiceEntryId,
  forwardingId,
  startAt,
  finishAt,
}: UpdateAuctionDatesProps) => {
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [confirmData, setConfirmData] = useState<AuctionEntryUpdateDatesValidationSchema | null>(null);

  const {
    control,
    setError,
    handleSubmit,
    reset,
    formState: {
      errors,
      isDirty,
    },
  } = useForm<AuctionEntryUpdateDatesValidationSchema>({
    resolver: zodResolver(auctionEntryUpdateDatesSchema),
    defaultValues: {
      start_at_date: extractDateTime(startAt, 'date'),
      start_at_time: extractDateTime(startAt, 'time'),
      finish_at_date: extractDateTime(finishAt, 'date'),
      finish_at_time: extractDateTime(finishAt, 'time'),
    },
  });

  const {
    mutate: updateDates,
    isPending: updateDatesIsPending,
  } = useMaintenanceEstablishmentChoiceAuctionEntryUpdateDates({
    establishmentChoiceId: establishmentChoiceEntryId,
    startAt,
    finishAt,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['maintenanceForwarding', forwardingId],
        });

        queryClient.invalidateQueries({
          queryKey: ['maintenanceEstablishmentChoiceAuctionEntryUpdatedSummary', establishmentChoiceEntryId],
        });

        setConfirmData(null);

        setIsOpen(false);
      },
      onError: (error) => {
        setConfirmData(null);

        FormTransformers.errorsTransformer({
          setError,
          setMessages,
        })(error);
      },
    },
  });

  const onSubmit: SubmitHandler<AuctionEntryUpdateDatesValidationSchema> = (data) => {
    const changes = buildAuctionDatesChanges(data, startAt, finishAt);

    if (changes.length === 0)
      return;

    setConfirmData(data);
  };

  const handleConfirm = () => {
    if (confirmData)
      updateDates(confirmData);
  };

  useEffect(() => {
    reset({
      start_at_date: extractDateTime(startAt, 'date'),
      start_at_time: extractDateTime(startAt, 'time'),
      finish_at_date: extractDateTime(finishAt, 'date'),
      finish_at_time: extractDateTime(finishAt, 'time'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, startAt, finishAt]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title="Alterar datas do leilão"
        titleClass="text-2xl"
        bodyClassName="text-white-800"
        useCancellableFooter
        confirmButtonText="Aplicar alterações"
        confirmButtonClassName="bg-blue-700 rounded-br-lg disabled:opacity-50 disabled:cursor-not-allowed"
        cancelButtonClassName="bg-white-200 rounded-bl-lg"
        confirmButtonDisabled={!isDirty}
        onConfirm={handleSubmit(onSubmit)}
        body={(
          <div className="flex flex-col gap-4">
            <p className="text-sm text-white-400">
              Altere a data de início e/ou a data de término do leilão.
            </p>

            <div className="flex flex-col gap-2">
              <p className="text-sm font-bold text-white-800">
                Início do leilão
              </p>

              <div className="flex gap-3">
                <DateInput
                  fieldName="start_at_date"
                  control={control}
                  label="Data de início"
                  className="flex-1"
                  errorMessage={errors?.start_at_date?.message}
                />

                <Controller
                  control={control}
                  name="start_at_time"
                  render={({ field }) => (
                    <TextField
                      label="Horário de início"
                      type="time"
                      className="flex-1"
                      LeftIcon={<ClockIcon className="w-5 h-5 text-white-400" />}
                      {...field}
                      errorMessage={errors?.start_at_time?.message}
                    />
                  )}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-sm font-bold text-white-800">
                Fim do leilão
              </p>

              <div className="flex gap-3">
                <DateInput
                  fieldName="finish_at_date"
                  control={control}
                  label="Data de término"
                  className="flex-1"
                  errorMessage={errors?.finish_at_date?.message}
                />

                <Controller
                  control={control}
                  name="finish_at_time"
                  render={({ field }) => (
                    <TextField
                      label="Horário de término"
                      type="time"
                      className="flex-1"
                      LeftIcon={<ClockIcon className="w-5 h-5 text-white-400" />}
                      {...field}
                      errorMessage={errors?.finish_at_time?.message}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        )}
      />

      <ConfirmUpdateAuctionDates
        isOpen={!!confirmData}
        setIsOpen={(value) => {
          if (!value)
            setConfirmData(null);
        }}
        changes={confirmData ? buildAuctionDatesChanges(confirmData, startAt, finishAt) : []}
        isLoading={updateDatesIsPending}
        onConfirm={handleConfirm}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
}

export default UpdateAuctionDates;
